import React, { useEffect, useState, forwardRef, useImperativeHandle, useMemo, useCallback, useRef } from 'react';
import './index.scss';
import CardCommon from '@sg/components/CardCommon';
import { rendTitle, renderRichText } from '@sg/utils/commonPageMethod';
import { buildUpdateTime } from '@sg/utils/common';
import TinyEditor from '@sg/components/TinyEditor';
import https from '@sg/utils/httpC';
import Loading from '@/pages/intelligence/components/Loading';
import { useIntl, useSelector } from 'umi';
import { Button, notification } from 'tea-component';
import { useDeepCompareEffect } from 'ahooks';
import moment from 'moment';
import TableForm from '@sg/components/TableForm';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import COSFileList from '@sg/components/COSFileList';
import UploadFileList from '@sg/components/UploadFileList';
import { usePlainTextAutoSave } from './hooks/usePlainTextAutoSave';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import { filterFontSizeReviewByKey } from '@sg/utils/commonPageMethod';

interface PlainTextProps {
  url: string; // API endpoint to fetch data from
  editUrl: string; // API endpoint to save data to
  data?: string; // Optional HTML or plain text to render (optional when using url)
  title?: string | React.ReactNode; // Optional title, default 'Appendix & Notes'
  params?: any; // Optional parameters for the API call
  showDatePicker?: boolean; // Whether to show the date picker
  selectedMonth?: moment.Moment | null; // Selected month from parent component (controlled mode)
  maxDataTime?: string | null; // Maximum available data time from parent (YYYY-MM format)
  editable?: boolean; // Whether the content is editable 控制内容是否可以被编辑
  onSave?: (newData: string) => void; // Callback when content is saved 内容保存时回调
  onEditingChange?: (isEditing: boolean) => void; // 编辑状态变化回调
  onEditClick?: (type?: 'Edit Button' | 'Add Content Link') => void; // Callback when edit button or add content link is clicked 编辑按钮或添加内容链接点击回调
  userPermissions?: {
    canEdit?: boolean; // User permission to edit the content 控制用户是否有权限编辑内容
    // 在没有编辑权限且data为空时否是隐藏
    noAccessHide?: boolean;
  };
  showTimeInTitle?: boolean; // Whether to show time in title 是否在标题中显示时间
  className?: string;
  style?: React.CSSProperties; // 外层容器样式
  tinyEditorHeight?: number; // 富文本编辑器高度
  tipString?: string; // 提示文字
  // Auto-save related props
  autoSavePageKey?: string; // Unique identifier for auto-save (e.g., from EDIT_PAGE_KEY_MAP)
  autoSaveId?: string | number; // Additional identifier (e.g., project_id, studio_id)
  // File upload related props
  allowFileUpload?: boolean; // Whether to allow file upload (default: false)
  uploadDomain?: string; // COS domain for file upload
  logKeys?: {
    previewLogKey: string;
    downloadLogKey: string;
    shareLogKey: string;
  };
  cosConfigDomain?: string; // Custom COS domain parameter for getBIDSTencentQCouldMassage
  extraPreviewActions?: React.ReactNode; // Extra actions shown beside title/date in preview mode
}

// File type definition for PlainText component
export interface PlainTextFile {
  name: string; // File name
  path: string; // COS path for preview/download
  format?: string; // File extension (e.g., 'pdf', 'doc')
  size?: number | string; // File size
  [key: string]: any; // Allow other properties
}

const fetchData = async (payload: { url: string; params: any; }) => {
  const { url, params } = payload || {};
  return https({
    url,
    method: 'post',
    data: params,
    rawData: true,  // 添加 rawData 参数，与其他 API 调用保持一致
  });
};

const PlainText = forwardRef<any, PlainTextProps>(({
  data: propData,
  title = 'Appendix & Notes',
  url,
  editUrl,
  params,
  showDatePicker = false,
  selectedMonth,
  maxDataTime,
  editable = false,
  onSave = (newData) => {
    console.log('newData save', newData);
  },
  onEditingChange,
  onEditClick,
  userPermissions = { canEdit: false },
  showTimeInTitle = false,
  className,
  style,
  tinyEditorHeight = 250,
  tipString,
  autoSavePageKey,
  autoSaveId,
  allowFileUpload = false,
  uploadDomain = 'weekly_project_highlight',
  logKeys,
  cosConfigDomain,
  extraPreviewActions,
}, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<string>('');
  const { formatMessage } = useIntl();
  const [data, setData] = useState<string>(propData || '');
  const [time, setTime] = useState<any>(null);
  const [updateTime, setUpdateTime] = useState<string>(''); // Store update_time from API
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  // File state - managed internally, fetched from API
  const [files, setFiles] = useState<PlainTextFile[]>([]);
  const [editFiles, setEditFiles] = useState<PlainTextFile[]>([]);
  // Track original files for comparison (to detect changes)
  const originalFilesRef = useRef<PlainTextFile[]>([]);

  // Use ref to prevent infinite loops when handling file changes
  const lastFilesRef = useRef<string>('');

  const { userInfo } = useSelector((state: any) => ({
    userInfo: state?.home?.userInfo,
  }));
  const { userid = '' } = userInfo || {};

  const isModuleHide = useMemo(() => (!data || data.trim() === '') && !userPermissions.canEdit && userPermissions.noAccessHide, [data, userPermissions]);

  // Auto-save and route prompt hook - handles all auto-save and unsaved changes detection
  const {
    autoSaveVisible,
    setAutoSaveVisible,
    pageKeyCur,
    checkAutoSaveBeforeEdit,
    getAutoSavedValues,
    clearAutoSave,
    hasUnsavedChanges,
  } = usePlainTextAutoSave({
    autoSavePageKey,
    autoSaveId,
    data,
    time,
    editData,
    isEditing,
    editFiles,
    originalFiles: originalFilesRef.current,
  });

  // Memoize file upload callback to prevent infinite loops
  const handleFilesChange = useCallback((updatedFiles: PlainTextFile[]) => {
    // Include both path AND name in the key since name can change independently
    const filesKey = updatedFiles.map(f => `${f.path}|${f.name}`).join(',');
    if (filesKey !== lastFilesRef.current) {
      lastFilesRef.current = filesKey;
      setEditFiles(updatedFiles);
    }
  }, []);

  // Memoize files to prevent UploadFileList from detecting changes
  const memoizedFiles = useMemo(() => editFiles || [], [
    (editFiles || []).length,
    (editFiles || []).map(f => `${f.path}|${f.name}`).join(',')
  ]);

  // Memoize display files for view mode
  const memoizedDisplayFiles = useMemo(() => files || [], [
    (files || []).length,
    (files || []).map(f => `${f.path}|${f.name}`).join(',')
  ]);

  // 提取 fetchDataFromAPI 函数，以便在保存后重新调用
  // customParams 用于覆盖默认的 params（例如切换日期时）
  const fetchDataFromAPI = async (customParams?: any) => {
    // Build params based on whether selectedMonth is provided
    let requestParams;

    if (customParams) {
      // If custom params are provided, use them directly
      requestParams = customParams;
    } else if (selectedMonth !== undefined && selectedMonth !== null) {
      // If selectedMonth is provided and not null, remove logical_flag and add date
      const { logical_flag, ...restParams } = params || {};
      const dateStr = selectedMonth.format('YYYY-MM-DD');
      requestParams = { ...restParams, date: dateStr };
    } else if (showDatePicker && time) {
      // If showDatePicker is enabled and we have a time value, use it as date instead of logical_flag
      const { logical_flag, ...restParams } = params || {};
      const dateStr = time.format('YYYY-MM-DD');
      requestParams = { ...restParams, date: dateStr };
    } else {
      // Otherwise, use original params (including logical_flag)
      requestParams = params || {};
    }

    // Wait for params to be ready before calling API
    // Check if any param value is empty/undefined/null (except for optional flags like logical_flag)
    const optionalKeys = ['logical_flag']; // Keys that are optional and don't need to be present
    const hasEmptyRequiredParams = Object.entries(requestParams).some(([key, value]) => {
      if (optionalKeys.includes(key)) return false;
      return value === undefined || value === null || value === '';
    });

    // If params object is empty or has empty required values, don't call the API yet
    if (Object.keys(requestParams).length === 0 || hasEmptyRequiredParams) {
      return;
    }

    if (url && !propData) {
      setLoading(true);
      setError('');
      try {
        const response = await fetchData({ url, params: requestParams });
        const { data } = response as any;

        if (data) {

          const filterData = filterFontSizeReviewByKey(data.note);
          setData(filterData);
          // 如果在编辑模式，也要更新 editData
          if (isEditing) {
            setEditData(filterData);
          }
          if (data.date) {
            setTime(moment(data.date));
          }
          // Extract update_time from API response and format to YYYY-MM-DD
          // if (data.update_time) {
          //   setUpdateTime(moment(data.update_time).format('YYYY-MM-DD'));
          // }
          // Handle files from API response (backend returns 'attach' instead of 'files')
          if (allowFileUpload) {
            // Parse attach if it's a string (JSON), otherwise use as array
            let apiFiles: PlainTextFile[] = [];
            if (data.attach) {
              if (typeof data.attach === 'string') {
                try {
                  apiFiles = JSON.parse(data.attach);
                } catch (e) {
                  apiFiles = [];
                }
              } else if (Array.isArray(data.attach)) {
                apiFiles = data.attach;
              }
            }
            // Always update files state (clear if attach is empty/missing)
            setFiles(apiFiles);
            // Always update editFiles when fetching new data (even in edit mode)
            // This is needed when user changes date picker - we want to show the files for the new date
            setEditFiles(apiFiles);
            originalFilesRef.current = apiFiles;
          }
        } else {
          notification.error({
            description: 'No data received',
          });
          setError('No data received from API');
        }
      } catch (err) {
        notification.error({
          description: 'Failed to load data',
        });
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    }
  };

  // Split the text into an array by line breaks (\n) and filter out empty lines
  const paragraphs = data.split(/\r?\n/).filter(line => line.trim() !== '');

  // Check if the title is 'Appendix & Notes' and translate it if so.
  let baseTitle = title;
  if (title === 'Appendix & Notes') {
    baseTitle = formatMessage({ id: 'dbs_appendix_notes' });
  }

  // 根据 showTimeInTitle 和 isEditing 决定显示格式
  let displayTitle;
  if (showTimeInTitle && time && !isEditing) {
    // 在非编辑模式下且需要显示时间，在标题后显示时间
    displayTitle = rendTitle({ title: typeof baseTitle === 'string' ? baseTitle : '', tips: time.format('YYYY-MM'), tipString });
    // } else if (updateTime) {
    //   // 非编辑模式且有 update_time：显示 "Updated on {datetime}"
    //   displayTitle = rendTitle({ title: typeof baseTitle === 'string' ? baseTitle : '', tips: buildUpdateTime(updateTime), tipString });
  } else {
    // 其他情况：只显示标题和提示文字
    displayTitle = rendTitle({ title: typeof baseTitle === 'string' ? baseTitle : '', tipString });
  }

  // Enter edit mode (actual function that sets state)
  const enterEditMode = () => {
    setEditData(data);
    // Copy current files to editFiles for editing
    setEditFiles([...files]);
    originalFilesRef.current = [...files];
    setIsEditing(true);
    // 通知父组件状态变化
    if (onEditingChange) {
      onEditingChange(true);
    }
  };

  // Check for auto-save before entering edit mode
  const handleEdit = () => {
    if (checkAutoSaveBeforeEdit()) {
      // Auto-save exists, modal will be shown by the hook
      return;
    }
    enterEditMode();
  };

  // User chose to recover auto-saved content
  const handleAutoSaveConfirm = () => {
    const { note, date, files } = getAutoSavedValues();
    setEditData(note);
    if (date) {
      setTime(date);
    }
    // Restore auto-saved files
    if (files) {
      setEditFiles(files);
    }
    setAutoSaveVisible(false);
    setIsEditing(true);
    if (onEditingChange) {
      onEditingChange(true);
    }
  };

  // User chose to discard auto-saved content
  const handleAutoSaveCancel = () => {
    setAutoSaveVisible(false);
    clearAutoSave();
    enterEditMode();
  };

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    handleEdit,
    handleSave,
    isEditing,
    editData,
    data,
    refresh: fetchDataFromAPI, // Expose refresh method to parent
  }));

  // Sync selectedMonth from parent to internal time state
  useEffect(() => {
    if (selectedMonth !== undefined) {
      setTime(selectedMonth);
    }
  }, [selectedMonth]);

  // Fetch data from API if URL is provided
  // 使用 useDeepCompareEffect 防止 params 对象引用变化导致重复请求
  useDeepCompareEffect(() => {
    fetchDataFromAPI();
  }, [url, params, propData, selectedMonth]);

  // If no URL and no data provided, return nothing
  if (!url && !propData) {
    return null;
  }

  // If loading, show loading component (same CardCommon classes + header shell as loaded state
  // so parent layouts e.g. Live Titles column flex/width rules still apply)
  if (loading) {
    return (
      <CardCommon className={`dbs-plain-text ${className}`} style={style}>
        <div className="dbs-plain-text-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{displayTitle}</span>
          </div>
        </div>
        <div className="dbs-plain-text-content-wrapper">
          <div className="dbs-plain-text-content-data">
            <Loading />
          </div>
        </div>
      </CardCommon>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <CardCommon className={`dbs-plain-text ${className}`} style={style}>
        <div className="dbs-plain-text-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{displayTitle}</span>
          </div>
        </div>
        <div className="dbs-plain-text-content-wrapper">
          <p style={{ color: 'red' }}>{error}</p>
        </div>
      </CardCommon>
    );
  }

  const handleSave = async () => {
    // 构造返回数据格式
    const saveData: any = {
      note: editData,
    };

    if (params?.project_id !== undefined) {
      saveData.project_id = params.project_id;
    }

    if (params?.studio_id !== undefined) {
      saveData.studio_id = params.studio_id;
    }

    if (params?.game_title !== undefined) {
      saveData.game_title = params.game_title;
    }

    if (params?.source) {
      saveData.source = params.source;
    }
    // 如果有日期，添加 date 字段
    // Priority: 1) selectedMonth from parent, 2) time from API/picker, 3) maxDataTime from parent, 4) first day of current month
    if (selectedMonth !== undefined && selectedMonth !== null) {
      // If selectedMonth is provided and not null, use it
      saveData.date = selectedMonth.format('YYYY-MM-DD');
    } else if (time) {
      // If we have time (from API response or date picker), use it
      saveData.date = time.format('YYYY-MM-DD');
    } else if (showDatePicker) {
      // If showDatePicker is true but no time, default to first day of current month
      saveData.date = moment().startOf('month').format('YYYY-MM-DD');
    } else if (maxDataTime) {
      // If showDatePicker is false but maxDataTime is provided, use maxDataTime
      saveData.date = moment(maxDataTime, 'YYYY-MM').format('YYYY-MM-DD');
    } else {
      // Final fallback: default to first day of current month
      saveData.date = moment().startOf('month').format('YYYY-MM-DD');
    }

    // Include attach if file upload is enabled (backend expects 'attach' instead of 'files')
    if (allowFileUpload) {
      saveData.attach = editFiles.map(f => ({
        name: f.name,
        path: f.path,
        // size: f.size,
      }));
    }

    try {
      // 如果传入了 editUrl，直接发送请求
      if (editUrl) {
        setLoading(true);
        await https({
          url: editUrl,
          method: 'post',
          data: saveData,
          rawData: true,
        });
        // Fetch fresh data from API after saving
        await fetchDataFromAPI();
      }

      // 无论是否有 editUrl，都调用 onSave 回调
      if (onSave) {
        onSave(saveData);
      }

      // 如果没有 editUrl，也要更新显示的数据
      if (!editUrl) {
        setData(editData);
        // Also update files state
        if (allowFileUpload) {
          setFiles([...editFiles]);
        }
      }

      // Clear auto-save data on successful save
      clearAutoSave();

      // NOTE: Don't manually update data here when editUrl exists
      // fetchDataFromAPI() already updated it with fresh data from API
      // Only update data when editUrl doesn't exist (local-only mode)

      setIsEditing(false);
      // 通知父组件状态变化
      if (onEditingChange) {
        onEditingChange(false);
      }

      // Show success notification
      notification.success({
        // description: formatMessage({ id: 'dbs_save_success' }) || 'Saved successfully',
        description: 'Operate Successfully',
      });
    } catch (err: any) {
      console.error('Failed to save data:', err);
      // Keep edit mode and existing content; only surface the failure via toast.
      notification.error({
        description: err?.msg || 'Failed to save data',
      });
      setLoading(false);
    } finally {
      if (editUrl) {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setEditData(data);
    // Reset editFiles to original files
    setEditFiles([...files]);
    // Clear auto-save data on cancel
    clearAutoSave();
    setIsEditing(false);
    // 通知父组件状态变化
    if (onEditingChange) {
      onEditingChange(false);
    }
    // Fetch fresh data after cancel
    // If selectedMonth is provided and not null, fetch with date; otherwise fetch with logical_flag
    if (selectedMonth !== undefined && selectedMonth !== null) {
      const { logical_flag, ...restParams } = params || {};
      const dateStr = selectedMonth.format('YYYY-MM-DD');
      fetchDataFromAPI({ ...restParams, date: dateStr });
    } else if (params?.logical_flag) {
      fetchDataFromAPI({ ...params });
    }
  };
  const canEdit = editable && userPermissions.canEdit;

  // 创建 formList 用于 TableForm
  const formList = showDatePicker && isEditing ? [
    {
      key: 'date',
      type: 'MonthPicker',
      componentConfig: {
        overlayClassName: 'dbs-month-picker-overlay',
        value: time,
        onChange: (value: any) => {
          setTime(value);
          // 使用新选择的日期重新请求数据
          const newDate = value ? value.format('YYYY-MM-DD') : null;
          if (newDate) {
            // 复制 params 但排除 logical_flag
            const { logical_flag, ...restParams } = params || {};
            const newParams = { ...restParams, date: newDate };
            fetchDataFromAPI(newParams);
          }
        },
      }
    }
  ] : [];

  return (
    /**
     * 当用户满足3个条件时，模块隐藏
     * 1、没有数据
     * 2、用户没有编辑权限
     * 3、模块需要在特定条件下（指前两个条件）隐藏
     */
    <>
      {isModuleHide ? null : (
        <CardCommon className={`dbs-plain-text ${className}`} style={style}>
          <div className="dbs-plain-text-header">
            <div className="dbs-plain-text-header__left">
              {displayTitle}
              {canEdit && !isEditing && extraPreviewActions}
              {showDatePicker && isEditing && (
                <TableForm formList={formList} />
              )}
            </div>
            <div className="dbs-plain-text-header__right">
              {canEdit && !isEditing && (
                <Button
                  onClick={(e) => {
                    e?.stopPropagation(); // Prevent collapse event
                    // Call onEditClick callback if provided (for buried point tracking)
                    if (onEditClick) {
                      onEditClick('Edit Button');
                    }
                    handleEdit();
                  }}
                  style={{ height: '30px', minWidth: '60px', fontSize: '12px' }}
                  className="dbs-gradient-btn"
                >
                  {formatMessage({ id: 'dbs_edit' })}
                </Button>
              )}
              {canEdit && isEditing && (
                <div className="dbs-plain-text-header-buttons">
                  <Button
                    onClick={(e) => {
                      e?.stopPropagation(); // Prevent collapse event
                      handleSave();
                    }}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_confirm' })}
                  </Button>
                  <Button
                    onClick={(e) => {
                      e?.stopPropagation(); // Prevent collapse event
                      handleCancel();
                    }}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-outline-btn"
                  >
                    {formatMessage({ id: 'dbs_cancel' })}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {isEditing ? (
            <div className="dbs-rich-text-editor">
              <TinyEditor
                value={editData}
                getEditorContent={(content) => setEditData(content)}
                isShowToolbar={true}
                minHeight={tinyEditorHeight}
                maxContentHeight={tinyEditorHeight}
                placeholder={formatMessage({ id: 'dbs_please_enter' })}
              />
              {/* File upload section in edit mode */}
              {allowFileUpload && (
                <div className="dbs-plain-text-files">
                  <UploadFileList
                    className='dbs-styled-upload-file-list'
                    uniqueId={`plain-text-${autoSavePageKey || 'default'}-${autoSaveId || ''}`}
                    uploadFolderName={uploadDomain}
                    defaultList={memoizedFiles as any}
                    onResult={handleFilesChange}
                    showFileList={true}
                    fileListPosition="right"
                    buttonTips=""
                    autoResizeInput={true}
                    multiple={true}
                    includeSizeInResult={false}
                  />
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="dbs-plain-text-content-wrapper">
                {data && data.trim() !== '' ? (
                  // <FormatHtmlTextToReact text={data} />
                  // <div className="dbs-plain-text-content" dangerouslySetInnerHTML={{ __html: data }} />
                  renderRichText(data, 'dbs-plain-text-content')
                ) : allowFileUpload &&
                  memoizedDisplayFiles &&
                  memoizedDisplayFiles.length > 0 ? (
                  // Attachment-only: match board governance empty text placeholder.
                  renderRichText('', 'dbs-plain-text-content')
                ) : (
                  <div className="dbs-plain-text-content-data">
                    {canEdit ? (
                      <div>
                        No Data. Please
                        <span onClick={() => {
                          // Call onEditClick callback if provided (for buried point tracking)
                          if (onEditClick) {
                            onEditClick('Add Content Link');
                          }
                          handleEdit();
                        }} style={{ cursor: 'pointer', color: '#7F6FF1', marginLeft: '4px' }}>Add Content</span>
                      </div>
                    ) : (
                      <div>No Data</div>
                    )}
                  </div>
                )}
                {/*  File list section  */}
                {allowFileUpload && memoizedDisplayFiles && memoizedDisplayFiles.length > 0 && (
                  <COSFileList
                    className='dbs-styled-cos-file-list'
                    domain={uploadDomain}
                    urlSearchParams={{
                      ...(params || {}),
                      // Pass cos_domain for the previewer to know which COS bucket to use
                      cos_domain: cosConfigDomain || uploadDomain,
                      // Pass scroll_target for the previewer to know which section to scroll to
                      scroll_target: 'notes',
                    }}
                    logKeys={logKeys}
                    enableActions={true}
                    cosConfigDomain={cosConfigDomain}
                    files={memoizedDisplayFiles.map(file => {
                      // Helper function to split filename and extension
                      const splitFileName = (fileName?: string) => {
                        if (!fileName || typeof fileName !== 'string') return { name: '', format: '' };
                        const lastDotIndex = fileName.lastIndexOf('.');
                        if (lastDotIndex <= 0) {
                          return { name: fileName, format: '' };
                        }
                        return {
                          name: fileName.substring(0, lastDotIndex),
                          format: fileName.substring(lastDotIndex) // Includes the dot
                        };
                      };

                      // Derive a safe file name: prefer provided name, else fallback to basename(path)
                      const rawName = (typeof file.name === 'string' && file.name)
                        || (typeof file.path === 'string' && file.path.split('/').pop())
                        || '';

                      // Prefer explicit format if provided; otherwise parse from name
                      const parsed = file.format ? { name: rawName, format: file.format } : splitFileName(rawName);
                      const fileNameWithoutExt = parsed.name || rawName;
                      const fileExtension = parsed.format || '';

                      // Ensure format doesn't have a dot at the beginning
                      const cleanFormat = (fileExtension || '').startsWith('.') ? fileExtension.substring(1) : (fileExtension || '');

                      return {
                        ...file,
                        name: fileNameWithoutExt,
                        format: cleanFormat, // Without the dot
                        // Convert size to number if it's a string
                        size: typeof file.size === 'string' ? parseInt(file.size) : file.size || 0,
                      };
                    })}
                  />
                )}
              </div>
            </>
          )}
        </CardCommon>
      )}

      {/* Auto-save recovery modal */}
      {autoSavePageKey && (
        <AutoSaveModal
          visible={autoSaveVisible}
          onConfirm={handleAutoSaveConfirm}
          onClose={handleAutoSaveCancel}
        />
      )}

      {/* RoutePrompt to intercept navigation when there are unsaved changes */}
      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          // Save changes before navigating away
          await handleSave();
          // Navigation will proceed after save completes
        }}
      />
    </>
  );
});

export default PlainText;
